import api from './api';
import {
  Attendance,
  MarkAttendanceData,
  AttendanceSummary,
  ApiResponse,
  Employee,
} from '../types';

export const attendanceService = {
  async markAttendance(data: MarkAttendanceData): Promise<Attendance> {
    const response = await api.post<ApiResponse<Attendance>>('/attendance', data);
    return response.data.data!;
  },

  async getMyAttendance(params?: {
    month?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<Attendance[]> {
    const queryParams = new URLSearchParams();
    if (params?.month) queryParams.append('month', String(params.month));
    if (params?.year) queryParams.append('year', String(params.year));
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await api.get<ApiResponse<Attendance[]>>(
      `/attendance?${queryParams}`
    );
    return response.data.data!;
  },

  async getAttendanceSummary(month?: number, year?: number, employeeId?: number): Promise<AttendanceSummary> {
    const queryParams = new URLSearchParams();
    if (month) queryParams.append('month', String(month));
    if (year) queryParams.append('year', String(year));
    
    // If employeeId is provided, use the employee-specific summary endpoint
    if (employeeId) {
      const response = await api.get<ApiResponse<AttendanceSummary>>(
        `/attendance/employee/${employeeId}/summary?${queryParams}`
      );
      return response.data.data!;
    }
    
    const response = await api.get<ApiResponse<AttendanceSummary>>(
      `/attendance/summary?${queryParams}`
    );
    return response.data.data!;
  },

  async getCompanyAttendanceSummary(month?: number, year?: number): Promise<{
    totalEmployees: number;
    present: number;
    absent: number;
    halfDay: number;
    leaveCL: number;
    leavePL: number;
    leaveUnpaid: number;
    holiday: number;
    employeesPresent: number;
    employeesAbsent: number;
    employeesOnCL: number;
    employeesOnPL: number;
  }> {
    const queryParams = new URLSearchParams();
    if (month) queryParams.append('month', String(month));
    if (year) queryParams.append('year', String(year));
    
    const response = await api.get<ApiResponse<{
      totalEmployees: number;
      present: number;
      absent: number;
      halfDay: number;
      leaveCL: number;
      leavePL: number;
      leaveUnpaid: number;
      holiday: number;
      employeesPresent: number;
      employeesAbsent: number;
      employeesOnCL: number;
      employeesOnPL: number;
    }>>(`/attendance/company-summary?${queryParams}`);
    return response.data.data!;
  },

  async getAllAttendance(params?: {
    month?: number;
    year?: number;
    employeeId?: number;
  }): Promise<Attendance[]> {
    const queryParams = new URLSearchParams();
    if (params?.month) queryParams.append('month', String(params.month));
    if (params?.year) queryParams.append('year', String(params.year));
    if (params?.employeeId) queryParams.append('employeeId', String(params.employeeId));
    
    const response = await api.get<ApiResponse<Attendance[]>>(
      `/attendance/all?${queryParams}`
    );
    return response.data.data!;
  },

  async markAttendanceForEmployee(
    employeeId: number,
    data: MarkAttendanceData
  ): Promise<Attendance> {
    const response = await api.post<ApiResponse<Attendance>>(
      `/attendance/employee/${employeeId}`,
      data
    );
    return response.data.data!;
  },

  async bulkImport(file: File): Promise<{ success: number; failed: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post<
      ApiResponse<{ success: number; failed: number; errors: string[] }>
    >('/attendance/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },

  async exportToCsv(month: number, year: number): Promise<Blob> {
    const response = await api.get(`/attendance/export?month=${month}&year=${year}`, {
      responseType: 'blob',
    });
    return response.data;
  },


  async getEmployeesByStatus(status: string): Promise<Employee[]> {
    const response = await api.get<ApiResponse<Employee[]>>(
      `/attendance/employees-by-status?status=${status}`
    );
    return response.data.data!;
  },

  async markAttendanceWithSelfie(
    selfieFile: File,
    location: { latitude: number; longitude: number },
    timestamp: Date
  ): Promise<Attendance> {
    const formData = new FormData();
    formData.append('selfie', selfieFile);
    formData.append('latitude', String(location.latitude));
    formData.append('longitude', String(location.longitude));
    formData.append('timestamp', timestamp.toISOString());

    const response = await api.post<ApiResponse<Attendance>>(
      '/attendance/selfie',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data.data!;
  },
};
